import {
  useState, useContext, useEffect, useCallback, useRef,
} from 'react';
import Head from 'next/head';
import Link from 'next/link';
import PropTypes from 'prop-types';
import { Header } from 'fjs-components';
import { getCADTool } from '@future-joinery-systems/fjs-cad';
import { useLazyQuery, useMutation } from '@apollo/client';
import { window } from 'rxjs/operators';
import * as Sentry from '@sentry/nextjs';
import { isEqual } from 'lodash';
import PriceDataContext from './helpers/contexts/PriceDataContext';
import useUser from '../lib/useUser';
import UserContext from './helpers/contexts/UserContext';
import ProjectContext from './helpers/contexts/ProjectContext';
import {
  DUPLICATE_AS_NEW,
  GET_PRODUCT_INSTANCES_BY_ID,
  REQUEST_BILL_OF_MATERIALS,
  GET_BILL_OF_MATERIALS_STATUS,
  CREATE_PROJECT, GET_USER_PROJECTS, CREATE_PRODUCT_INSTANCE, SAVE_PRODUCT_CONFIGURATION,
} from '../lib/sharedGQL';
import useGlobalState from './hooks/globalState';
import {
  getAdminFlagFromSketchupStorage,
  getSubscriptionFromSketchupStorage,
  getUserObjectFromSketchupStorage,
} from '../lib/sketchupStorage';
import { ProductInstanceSKP, BomStatus, BillOfMaterialsStatus } from '../lib/types';
import {
  waitForBomReady,
  requestBomIfNeeded,
  getProductInstanceForBom,
  BOM_BATCH_SIZE,
  STATE_UPDATE_DELAY_MS,
  BATCH_DELAY_MS,
  isBomPending,
  isBomReady,
  isBomFailed,
  MAX_BOM_RETRIES,
  isRetryableError,
} from './helpers/bomUtils';
import { captureSupportEvent, isSystemError, isUserFixableError } from '../lib/errorUtils';
import { ToastProvider } from './ToastProvider';

const pagesLoggedIn = [
  { name: 'My Products', link: '/products', id: Math.floor(Math.random() * 10e5) },
  { name: 'Catalogue', link: '/catalogue', id: Math.floor(Math.random() * 10e5) },
  { name: 'Logout', link: '/logout', id: Math.floor(Math.random() * 10e5) },
  {
    name: 'Give Feedback', link: 'https://coda.io/form/FJS-Configurator-Feedback_dlL4IZglHye', id: Math.floor(Math.random() * 10e5), openInNewTab: true,
  },
];
const pagesLoggedOut = [
  { name: 'Home', link: '/', id: Math.floor(Math.random() * 10e5) },
  { name: 'Login', link: '/login', id: Math.floor(Math.random() * 10e5) },
];

function normalise(input) {
  if (typeof input === 'string') {
    try {
      return JSON.parse(input);
    } catch {
      return null;
    }
  }
  return input;
}

function Layout({
  children, productsInSketchup, selectedProducts, appStatus, versionName, watchModel,
}) {
  const [globalPriceData, setGlobalPriceData] = useState({});

  // TODO: why are we using 2 different ways of getting the user? was the first line before we used globalState?
  const { user } = useUser();
  const userContext = useContext(UserContext);

  const [getUserProjects] = useLazyQuery(GET_USER_PROJECTS, { fetchPolicy: 'network-only' });
  const [createNewProject, { loading: createProjectLoading }] = useMutation(CREATE_PROJECT);
  const [project, setProject] = useState({ id: '' });
  const [checkingProducts, setCheckingProducts] = useState(false);
  const [checkingProject, setCheckingProject] = useState(false);

  // TODO: why do we use contexts rather than another key on global state for price and user?
  const [showDuplicateModal, setShowDuplicateModal] = useGlobalState('showDuplicateModal');
  const [checksComplete, setChecksComplete] = useGlobalState('checksComplete');
  const [isSaving, setIsSaving] = useGlobalState('isSaving');
  const [bomRetryState, setBomRetryState] = useGlobalState('bomRetryState');
  const [projectIdWithCompletedInitialCheck, setProjectIdWithCompletedInitialCheck] = useGlobalState('projectIdWithCompletedInitialCheck');

  const [prevProductsInSkp, setPrevProductsInSkp] = useState<ProductInstanceSKP[] | undefined>();
  const [prevProject, setPrevProject] = useState({ id: '' });
  const [prevAppStatus, setPrevAppStatus] = useState();
  const prevCheckingProductsRef = useRef(false);

  // Cache for user projects to avoid spam
  const [cachedUserProjects, setCachedUserProjects] = useState<Array<{ id: string }> | null>(null);
  const [lastCheckedPath, setLastCheckedPath] = useState<string | null>(null);

  const [duplicateProductInstanceAsNew] = useMutation(DUPLICATE_AS_NEW, {
    onCompleted: (response) => {
      console.log('duplicateProductInstanceAsNew complete', response);
    },
  });

  const [createProductInstance] = useMutation(CREATE_PRODUCT_INSTANCE, {
    onCompleted: (response) => {
      console.log('createProductInstance complete', response);
    },
  });

  const [saveProductInstanceConfiguration] = useMutation(SAVE_PRODUCT_CONFIGURATION, {
    onCompleted: (response) => {
      console.log('saveProductInstanceConfiguration complete', response);
    },
  });

  const [requestBillOfMaterials] = useMutation(REQUEST_BILL_OF_MATERIALS);
  const [getBillOfMaterialsStatus] = useLazyQuery(GET_BILL_OF_MATERIALS_STATUS);
  const [getProductInstancesById] = useLazyQuery(GET_PRODUCT_INSTANCES_BY_ID, {
    onCompleted: (response) => {
      console.log('Checked productsInSketchup against product instances in db', response);
    },
  });

  /**
   * Updates retry state for a failed BOM based on error type
   * @param retryState - Current retry state
   * @param error - Error object or error message
   * @param instanceId - Product instance ID
   * @param bomData - BOM data with productId and configuration
   * @param productName - Product name for logging
   * @returns Updated retry state and isRetryable flag
   */
  function updateRetryStateForFailure(
    retryState: { retryCount: number; retriesExhausted: boolean; isRetryable: boolean },
    error: Error | string,
    instanceId: string,
    bomData: { productId: string; configuration: string },
    productName: string,
  ): { updatedRetryState: typeof retryState & { sentryEventId?: string }; isRetryable: boolean } {
    const errorMessage = error instanceof Error ? error.message : error;
    const errorObj = error instanceof Error ? error : new Error(errorMessage);
    const isRetryable = isRetryableError(errorObj);
    const isSystemErr = isSystemError(errorObj);
    const isUserFixable = isUserFixableError(errorObj);

    const updatedRetryState = { ...retryState };

    if (isRetryable) {
      // Timeout errors: increment retry count and check if exhausted
      updatedRetryState.retryCount += 1;
      updatedRetryState.isRetryable = true;
      updatedRetryState.retriesExhausted = updatedRetryState.retryCount >= MAX_BOM_RETRIES;
    } else {
      // Non-retryable errors (like missing fitting or system errors): immediately exhausted
      updatedRetryState.retriesExhausted = true;
      updatedRetryState.isRetryable = false;
    }

    // Always capture to Sentry for all errors (for tracking and support)
    // Tag system errors with higher severity and attach support_code via captureSupportEvent
    const { eventId, supportCode } = captureSupportEvent(errorObj, {
      level: isSystemErr ? Sentry.Severity.Error : Sentry.Severity.Warning,
      extra: {
        productId: bomData.productId,
        configuration: bomData.configuration,
        instanceId,
        errorMessage, // Full detailed message
        isRetryable,
        isSystemError: isSystemErr,
        isUserFixable,
        retryCount: updatedRetryState.retryCount,
        retriesExhausted: updatedRetryState.retriesExhausted,
      },
    });

    // Store support code for all errors (so we can show support code to users)
    // Note: We store the supportCode (not the full eventId) in sentryEventId field since that's
    // what ProductDashboard uses for displaying the support code to users. The support code
    // matches the 'support_code' tag in Sentry, making it searchable.
    if (eventId && supportCode) {
      (updatedRetryState as any).sentryEventId = supportCode;
    }

    return { updatedRetryState, isRetryable };
  }

  /**
   * Verifies BOM status for a product instance and updates SketchUp definition
   * Handles BOM generation requests, polling for completion, and retry logic
   * @param existingProductInSKP - Product instance from SketchUp
   * @param cadTool - CAD tool interface for updating product definitions
   * @returns Promise resolving to result status, retryability, and retry exhaustion state
   */
  async function verifyBomAndUpdateSkpDefinition(existingProductInSKP: ProductInstanceSKP, cadTool): Promise<{ result: string; isRetryable: boolean; retriesExhausted: boolean }> {
    console.log('Layout/retrieveProductDataAndSaveToComponent: retrieving product data for instance', existingProductInSKP?.id);
    if (window === undefined) return { result: null, isRetryable: false, retriesExhausted: false };
    if (!project?.id) {
      console.log('Layout/retrieveProductDataAndSaveToComponent: no project id found, cannot retrieve metadata');
      return { result: null, isRetryable: false, retriesExhausted: false };
    }
    if (!existingProductInSKP?.id) {
      console.log('Layout/retrieveProductDataAndSaveToComponent: no product instance id found, cannot retrieve metadata');
      return { result: null, isRetryable: false, retriesExhausted: false };
    }

    const { instanceId } = existingProductInSKP;
    let retryState = bomRetryState?.[instanceId] || { retryCount: 0, retriesExhausted: false, isRetryable: false };

    console.log('Layout/retrieveProductDataAndSaveToComponent: retrieving metadata for', existingProductInSKP.productName, instanceId);

    try {
      // Get productId and configuration from instance
      const bomData = await getProductInstanceForBom(
        existingProductInSKP,
        async (dbInstanceId: string) => {
          // Fallback to database lookup if productId/configuration not available
          const dbResult = await getProductInstancesById({
            variables: { data: [dbInstanceId] },
          });
          const dbInstance = dbResult.data?.getProductInstancesById?.[0];
          if (dbInstance) {
            return {
              productId: dbInstance.productId || existingProductInSKP.productId || '',
              configuration: dbInstance.configuration || '',
            };
          }
          return null;
        },
      );

      if (!bomData) {
        console.log('Could not get productId and configuration for BOM request', existingProductInSKP);
        Sentry.captureMessage(`Could not get productId/configuration for instance ${instanceId}`);
        return { result: 'Failed', isRetryable: false, retriesExhausted: true };
      }

      // Request BOM if needed and wait for it to be ready
      const bomStatus = await requestBomIfNeeded(
        async (productId: string, configuration: string) => {
          const result = await requestBillOfMaterials({
            variables: { data: { productId, configuration } },
          });
          return result.data?.requestBillOfMaterials as BillOfMaterialsStatus;
        },
        async (productId: string, configuration: string) => {
          const result = await getBillOfMaterialsStatus({
            variables: { data: { productId, configuration } },
          });
          return result.data?.getBillOfMaterialsStatus as BillOfMaterialsStatus;
        },
        bomData.productId,
        bomData.configuration,
      );

      // If not ready, poll until ready (with timeout)
      let finalStatus = bomStatus;
      let isRetryable = false;
      if (bomStatus.status === 'PENDING') {
        try {
          finalStatus = await waitForBomReady(
            async (productId: string, configuration: string) => {
              const result = await getBillOfMaterialsStatus({
                variables: { data: { productId, configuration } },
              });
              return result.data?.getBillOfMaterialsStatus as BillOfMaterialsStatus;
            },
            bomData.productId,
            bomData.configuration,
          );
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Unknown error';
          console.log(`BOM generation failed or timed out for ${existingProductInSKP.productName}:`, errorMessage);

          // Update retry state based on error type
          const { updatedRetryState, isRetryable: isRetryableErr } = updateRetryStateForFailure(
            retryState,
            error,
            instanceId,
            bomData,
            existingProductInSKP.productName,
          );
          isRetryable = isRetryableErr;
          // Update local retryState to match what's stored in global state
          retryState = updatedRetryState;

          // Set status to FAILED
          finalStatus = {
            ...bomStatus,
            status: 'FAILED' as BomStatus,
            errorMessage, // Store full error message
          };

          // Update retry state in globalState
          setBomRetryState({
            ...bomRetryState,
            [instanceId]: {
              ...updatedRetryState,
              errorMessage, // Store error message for toast display
              sentryEventId: updatedRetryState.sentryEventId, // Store Sentry eventId for user-facing error message
            },
          });
        }
      } else if (bomStatus.status === 'FAILED') {
        // Handle immediate FAILED status (not from polling timeout)
        // This happens when requestBomIfNeeded returns FAILED immediately
        const errorMessage = bomStatus.errorMessage || 'BOM generation failed';
        console.log(`BOM generation failed immediately for ${existingProductInSKP.productName}:`, errorMessage);

        // Update retry state based on error type
        const { updatedRetryState, isRetryable: isRetryableErr } = updateRetryStateForFailure(
          retryState,
          errorMessage,
          instanceId,
          bomData,
          existingProductInSKP.productName,
        );
        isRetryable = isRetryableErr;
        // Update local retryState to match what's stored in global state
        retryState = updatedRetryState;

        // Update retry state in globalState
        setBomRetryState({
          ...bomRetryState,
          [instanceId]: {
            ...updatedRetryState,
            errorMessage, // Store error message for toast display
            sentryEventId: updatedRetryState.sentryEventId, // Store Sentry eventId for user-facing error message
          },
        });

        finalStatus = bomStatus;
      }

      // Update billOfMaterials flag based on status
      const statusValue: BomStatus = finalStatus.status;
      console.log('BOM status set to', statusValue, 'for', existingProductInSKP.productName, instanceId);

      const updatedProductInstance = {
        ...existingProductInSKP,
        projectId: project.id,
        billOfMaterials: statusValue,
        // Don't store retry state in metadata - it's in globalState now
      };

      await cadTool.updateProduct(
        updatedProductInstance,
        null, // keep existing geometry
        existingProductInSKP.id,
      ).toPromise();

      if (finalStatus.status === 'READY') {
        // Clear retry state on success
        const updatedRetryState = { ...bomRetryState };
        delete updatedRetryState[instanceId];
        setBomRetryState(updatedRetryState);
        return { result: 'Success', isRetryable: false, retriesExhausted: false };
      }
      if (finalStatus.status === 'FAILED') {
        // Log specific error to Sentry with full message
        Sentry.captureMessage(`BOM generation failed for ${existingProductInSKP.productName}`, {
          extra: {
            productId: bomData.productId,
            configuration: bomData.configuration,
            errorMessage: finalStatus.errorMessage, // Full detailed message
            isRetryable,
            retriesExhausted: retryState.retriesExhausted,
          },
        });
        return { result: 'Failed', isRetryable, retriesExhausted: retryState.retriesExhausted };
      }

      return { result: 'Pending', isRetryable: false, retriesExhausted: false };
    } catch (error) {
      console.error(`Error retrieving BOM status for ${existingProductInSKP.productName}:`, error);
      const errorMessage = error instanceof Error ? error.message : String(error);

      // Get current retry state (use existing if available, otherwise get from global state)
      const currentRetryState = retryState || bomRetryState?.[instanceId] || {
        retryCount: 0,
        retriesExhausted: false,
        isRetryable: false,
      };

      // Get bomData if available (might not be if error occurred before we retrieved it)
      // Use fallback values from existingProductInSKP if bomData wasn't retrieved yet
      const bomDataFallback = {
        productId: existingProductInSKP.productId || '',
        configuration: existingProductInSKP.configuration || '',
      };

      // Update retry state based on error type (consistent with inner error handling)
      // This also determines if the error is retryable
      const { updatedRetryState, isRetryable: isRetryableFromState } = updateRetryStateForFailure(
        currentRetryState,
        error,
        instanceId,
        bomDataFallback,
        existingProductInSKP.productName,
      );

      // Update retry state in globalState (consistent with inner error handling paths)
      setBomRetryState({
        ...bomRetryState,
        [instanceId]: {
          ...updatedRetryState,
          errorMessage, // Store error message for toast display
          sentryEventId: updatedRetryState.sentryEventId, // Store Sentry eventId for user-facing error message
        },
      });

      // Determine final BOM status based on retry state
      // For non-retryable errors or exhausted retries, mark as FAILED
      // For retryable errors with retries remaining, keep as PENDING to allow retry
      const shouldMarkAsFailed = !isRetryableFromState || updatedRetryState.retriesExhausted;
      const finalBomStatus: BomStatus = shouldMarkAsFailed ? 'FAILED' : 'PENDING';

      // Update SketchUp with the appropriate status
      // This ensures consistent state and prevents infinite loops
      const updatedProductInstance = {
        ...existingProductInSKP,
        projectId: project.id,
        billOfMaterials: finalBomStatus,
      };

      try {
        await cadTool.updateProduct(
          updatedProductInstance,
          null, // keep existing geometry
          existingProductInSKP.id,
        ).toPromise();

        console.log(
          'BOM status set to',
          finalBomStatus,
          'for',
          existingProductInSKP.productName,
          instanceId,
          shouldMarkAsFailed
            ? '(non-retryable or retries exhausted)'
            : '(retryable, will retry)',
        );
      } catch (updateError) {
        console.error('Failed to update SketchUp definition with BOM status:', updateError);
        Sentry.captureException(updateError, {
          extra: {
            instanceId,
            productName: existingProductInSKP.productName,
            originalError: errorMessage,
            attemptedStatus: finalBomStatus,
          },
        });
      }

      return {
        result: 'Failed',
        isRetryable: isRetryableFromState,
        retriesExhausted: updatedRetryState.retriesExhausted,
      };
    }
  }

  /**
   * Processes BOM verification for multiple product instances in batches
   * Prevents connection pool exhaustion by processing sequentially within batches
   * @param processedIds - Array of instance IDs that have already been processed (mutated)
   * @param productInstancesToRetrieveMetadata - Array of product instances needing BOM verification
   * @param cadTool - CAD tool interface for updating product definitions
   */
  async function batchVerifyBomAndUpdateSkp(processedIds: string[], productInstancesToRetrieveMetadata: ProductInstanceSKP[], cadTool) {
    console.log('Retrieving metadata safely...', { processedIds, productInstancesToRetrieveMetadata });

    // Filter out already processed instances
    const remaining = productInstancesToRetrieveMetadata.filter((i) => !processedIds.includes(i.instanceId));

    if (remaining.length === 0) {
      console.log('All instances processed');
      return;
    }

    // Process in batches to avoid connection pool exhaustion
    for (let i = 0; i < remaining.length; i += BOM_BATCH_SIZE) {
      const batch = remaining.slice(i, i + BOM_BATCH_SIZE);
      console.log(`Processing batch of ${batch.length} instances (${i + 1}-${Math.min(i + BOM_BATCH_SIZE, remaining.length)} of ${remaining.length})`);

      // Process batch sequentially (not in parallel) to avoid connection issues
      // eslint-disable-next-line no-await-in-loop
      for (const prod of batch) {
        try {
          // eslint-disable-next-line no-await-in-loop
          const result = await verifyBomAndUpdateSkpDefinition(prod, cadTool);
          processedIds.push(prod.instanceId);
          console.log(`Processed instance ${prod.instanceId}: ${result.result}`);
        } catch (error) {
          console.error(`Error processing instance ${prod.instanceId}:`, error);
          Sentry.captureException(error, {
            extra: {
              instanceId: prod.instanceId,
              productName: prod.productName,
            },
          });
          // Still mark as processed to avoid infinite loops, but log the error
          processedIds.push(prod.instanceId);
        }
      }

      // Small delay between batches to avoid overwhelming the connection pool
      if (i + BOM_BATCH_SIZE < remaining.length) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise<void>((resolve) => {
          setTimeout(() => resolve(), 100);
        });
      }
    }

    console.log(`batchVerifyBomAndUpdateSkp: processed ${processedIds.length} of ${productInstancesToRetrieveMetadata.length} instances`);
  }

  useEffect(() => {
    (async () => {
      const skpUser = await getUserObjectFromSketchupStorage();
      if (skpUser) {
        const isAdmin = await getAdminFlagFromSketchupStorage();
        const subscription = await getSubscriptionFromSketchupStorage();
        userContext.setUserLoggedIn({
          isLoggedIn: !!skpUser, isAdmin: isAdmin === 'true', isLoaded: true, subscription,
        });
      } else {
        userContext.setUserLoggedIn({
          isLoggedIn: false, isAdmin: false, isLoaded: true, subscription: 'Inactive',
        });
      }
    })();
  }, []);

  // Invalidate user projects cache when user login state changes
  useEffect(() => {
    const currentLoginState = userContext?.userLoggedIn?.isLoggedIn ?? false;
    setCachedUserProjects(null);
  }, [userContext?.userLoggedIn?.isLoggedIn]);

  // Check and establish project - runs when needed, not on every appStatus change
  const checkProject = useCallback(async () => {
    if (checkingProject || createProjectLoading) return;
    if (!userContext?.userLoggedIn?.isLoggedIn) return;
    if (!appStatus) return;

    setCheckingProject(true);
    try {
      console.log('Checking Project');

      const cadTool = getCADTool();
      const projectId = await cadTool.getFileMeta('projectId').toPromise();
      const savedProjectPath = await cadTool.getFileMeta('projectPath').toPromise();
      const currentFilePath = await cadTool.getFilePath().toPromise();
      const currentProjectPath = currentFilePath || appStatus?.current_path || '';

      console.info('savedProjectPath', savedProjectPath);
      console.info('currentProjectPath', currentProjectPath);

      // For unsaved files (empty path), use a special marker to track that we've checked
      const pathKey = currentProjectPath || '__UNSAVED__';

      // Check if path has changed FIRST (before early return) - this handles "Save As" scenarios
      // Check if path has changed (only if both paths exist - handles saved file changes)
      const pathHasChanged = savedProjectPath && currentProjectPath && savedProjectPath !== currentProjectPath;

      // If we've already checked this path AND there's no path change, skip (unless project is missing)
      if (!pathHasChanged && lastCheckedPath === pathKey && projectId && project.id) {
        console.log('Already checked this path, skipping');
        return;
      }

      // Update saved path if file was just saved (going from unsaved to saved)
      // Keep the same projectId when transitioning from unsaved to saved
      if (!savedProjectPath && currentProjectPath) {
        console.log('File was just saved, updating savedProjectPath but keeping projectId');
        await cadTool.setFileMeta('projectPath', currentProjectPath);
      }

      let shouldCreateNewProject = false;

      // Handle path change (Save As scenario)
      if (pathHasChanged) {
        console.log('Path mismatch detected, will create new project');
        // Clear projectId to force new project creation
        if (projectId) {
          await cadTool.setFileMeta('projectId', '');
        }
        setProject({ id: '' });
        shouldCreateNewProject = true;
      } else if (projectId) {
        // Verify project belongs to user
        let userProjects: Array<{ id: string }> = [];
        if (cachedUserProjects) {
          console.log('Using cached user projects');
          userProjects = cachedUserProjects;
        } else {
          console.log('Fetching user projects');
          const userProjectsData = await getUserProjects();
          if (Array.isArray(userProjectsData.data?.getUserProjects)) {
            userProjects = userProjectsData.data.getUserProjects;
            setCachedUserProjects(userProjects);
          }
        }

        // If userProjects is empty, we can't verify ownership - create new project to be safe
        // If userProjects has items but projectId is not in the list, it doesn't belong to user
        if (userProjects.length === 0 || !userProjects.map((proj) => proj.id).includes(projectId)) {
          if (userProjects.length === 0) {
            console.log('Cannot verify project ownership (empty user projects list), creating new project');
          } else {
            console.log('Project does not belong to user, create new');
          }
          shouldCreateNewProject = true;
        } else if (project.id !== projectId) {
          console.log('Correct project, setting id');
          setProject({ id: projectId });
        }
      } else if (!project.id) {
        console.log('No project ID found, creating new project');
        shouldCreateNewProject = true;
      }

      if (shouldCreateNewProject) {
        console.log('About to create new project');

        const newProject = await createNewProject({ variables: { data: {} } });
        console.log('NEW PROJECT ID');
        console.log(newProject.data.createProject);
        const idSet = await cadTool.setFileMeta('projectId', newProject.data.createProject.id).toPromise();
        // Only set projectPath if we have a path (don't set for unsaved files)
        if (currentProjectPath) {
          const pathSet = await cadTool.setFileMeta('projectPath', currentProjectPath).toPromise();
          console.log('pathSet', pathSet);
        }
        console.log('idSet', idSet);

        const createdProject = newProject.data.createProject;
        setProject(createdProject);
        // Add to cache immediately
        if (cachedUserProjects) {
          setCachedUserProjects([...cachedUserProjects, createdProject]);
        } else {
          getUserProjects().then((userProjectsData) => {
            if (Array.isArray(userProjectsData.data?.getUserProjects)) {
              setCachedUserProjects(userProjectsData.data.getUserProjects);
            }
          });
        }
        // Trigger product checks
        setChecksComplete(false);
      }

      // Only update lastCheckedPath after all operations complete successfully
      // This prevents duplicate checks if an error occurs during the check process
      setLastCheckedPath(pathKey);
    } finally {
      setCheckingProject(false);
    }
  }, [userContext?.userLoggedIn?.isLoggedIn, appStatus, project.id, checkingProject, createProjectLoading, cachedUserProjects, lastCheckedPath]);

  // Check project when appStatus changes (but only if we don't have a project yet or path changed)
  // Note: We check even if current_path is empty (unsaved files) to ensure project is initialized
  useEffect(() => {
    if (!userContext?.userLoggedIn?.isLoggedIn) return;

    // Get current path (may be empty for unsaved files)
    const currentPath = appStatus?.current_path || '';
    const pathKey = currentPath || '__UNSAVED__';

    // Only check if we don't have a project or if path might have changed
    if (!project.id || lastCheckedPath !== pathKey) {
      checkProject();
    }
  }, [appStatus?.current_path, userContext?.userLoggedIn?.isLoggedIn, project.id, checkProject, lastCheckedPath]);

  // Periodic path check to catch Save As operations
  useEffect(() => {
    if (!userContext?.userLoggedIn?.isLoggedIn) return;
    if (typeof window === 'undefined') return;

    const intervalId = setInterval(async () => {
      const cadTool = getCADTool();
      const savedPath = await cadTool.getFileMeta('projectPath').toPromise();
      const currentPath = await cadTool.getFilePath().toPromise();

      if (savedPath && currentPath && savedPath !== currentPath && lastCheckedPath !== currentPath) {
        console.log('Periodic check detected path change, triggering project check');
        setLastCheckedPath(null); // Reset to force check
        checkProject();
      }
    }, 2000);

    // eslint-disable-next-line consistent-return
    return () => {
      clearInterval(intervalId);
    };
  }, [userContext?.userLoggedIn?.isLoggedIn, checkProject, lastCheckedPath]);

  const checkAndUpdateProducts = async () => {
    console.log('Executing Check and Update Products...');

    try {
      setCheckingProducts(true);

      const cadTool = getCADTool();
      const productInstancesToCreate = [];
      const productInstancesToDuplicate = [];
      const productInstancesToSave = [];
      const productInstancesToRetrieveMetadata: ProductInstanceSKP[] = [];
      const readyProductsToUpdateToPending: ProductInstanceSKP[] = [];
      const needsInitialCheck = projectIdWithCompletedInitialCheck !== project.id;
      const productInstanceIdsInSkp = productsInSketchup.filter((p) => !p.id.includes('#')).map((prod) => prod.id);

      const productInstancesInDbData = await getProductInstancesById({
        variables: {
          data: productInstanceIdsInSkp,
        },
      });
      const productInstancesInDB = productInstancesInDbData.data?.getProductInstancesById;

      let n = 1;
      for (const prodInst of productsInSketchup) {
        console.log(`CHECKING PRODUCT INSTANCE ${n} of ${productsInSketchup.length}: ${prodInst.id}, ${prodInst.productName}`);
        // check if product instance is in database
        if (!productInstancesInDB.map((i) => i.id).includes(prodInst.id)) {
          // check if id has been affected by make unique
          if (prodInst.id.includes('#') && productInstancesInDB.map((i) => i.id).includes(prodInst.id.slice(0, prodInst.id.indexOf('#')))) {
            productInstancesToDuplicate.push(prodInst);
            console.log(`Instance has been made unique and needs duplicating in the database - ${prodInst.id}, ${prodInst.productName}`);
          } else {
            productInstancesToCreate.push(prodInst);
            console.log(`Instance not found in database and needs to be created as a new instance in the database - ${prodInst.id}, ${prodInst.productName}`);
          }
        } else {
          const matchInDB = productInstancesInDB.find((p) => p.id === prodInst.id);
          console.log(`### Instance found in database - ${prodInst.id}, ${prodInst.productName}`);
          // check if current projectId matches the projectId of this instance in the database
          // eslint-disable-next-line no-lonely-if
          if (!prodInst.projectId) {
            productInstancesToDuplicate.push(prodInst);
            console.log(`Instance has no project and needs duplicating with current project id - ${prodInst.id}, ${prodInst.productName}`);
          } else if (prodInst.projectId !== project.id) {
            productInstancesToDuplicate.push(prodInst);
            console.log(`Instance belongs to a different project and needs duplicating with current project id - ${prodInst.id}, ${prodInst.productName}`);
          } else if (
            prodInst.configuration !== matchInDB.configuration
            || (prodInst.properties && matchInDB.properties && !isEqual(normalise(prodInst.properties), normalise(matchInDB.properties))) // old instances can return null or undefined properties
          ) {
            productInstancesToSave.push(prodInst);
            console.log(`Instance configuration doesn't match the instance in the database so database will be overwritten - ${prodInst.id}, ${prodInst.productName}`);
          } else {
            // check for metadata/BOM status
            // Determine which products need BOM retrieval
            const { instanceId } = prodInst;
            const retryState = bomRetryState?.[instanceId];
            const isFailed = isBomFailed(prodInst.billOfMaterials);
            const isPending = isBomPending(prodInst.billOfMaterials);

            // Always retry PENDING products
            if (isPending) {
              productInstancesToRetrieveMetadata.push(prodInst);
              console.log(`Instance BOM status is ${prodInst.billOfMaterials || 'undefined'}, needs to be retrieved - ${prodInst.id}, ${prodInst.productName}`);
            } else if (isFailed) {
              // For FAILED products:
              // Only retry if it's a retryable error (timeout) and retries not exhausted
              // Non-retryable errors (like missing fitting) are not retried
              // On page load, retry state is empty, so timeout errors will retry with normal mechanism
              const shouldRetry = !retryState // First time seeing this failure - allow retry (will determine if retryable)
                || (retryState.isRetryable && !retryState.retriesExhausted); // Retryable and not exhausted

              if (shouldRetry) {
                productInstancesToRetrieveMetadata.push(prodInst);
                console.log(`Instance BOM status is FAILED, ${retryState ? `retry count: ${retryState.retryCount}/${MAX_BOM_RETRIES}` : 'will check if retryable'} - ${prodInst.id}, ${prodInst.productName}`);
              } else {
                console.log(`Instance BOM status is FAILED, retries exhausted or non-retryable - ${prodInst.id}, ${prodInst.productName}`);
              }
            } else {
              // Verify READY products on initial check to catch stale statuses
              const isReady = isBomReady(prodInst.billOfMaterials);

              if (needsInitialCheck && isReady) {
                productInstancesToRetrieveMetadata.push(prodInst);
                readyProductsToUpdateToPending.push(prodInst);
                console.log(`Instance BOM status is READY, verifying on initial load for project ${project.id} - ${prodInst.id}, ${prodInst.productName}`);
              } else {
                console.log(`Instance is good to go! - ${prodInst.id}, ${prodInst.productName}`);
              }
            }
          }
        }
        n++;
      }

      console.log('To Create:       ', productInstancesToCreate.length);
      console.log('To Duplicate:    ', productInstancesToDuplicate.length);
      console.log('To Save:         ', productInstancesToSave.length);
      console.log('To Get Metadata: ', productInstancesToRetrieveMetadata.length);

      // set checksComplete to false if any instances require updates
      if (productInstancesToCreate.length || productInstancesToDuplicate.length || productInstancesToSave.length || productInstancesToRetrieveMetadata.length) {
        setChecksComplete(false);

        // Update READY products to PENDING to prevent infinite loop
        // Only do this if we're continuing (not returning early)
        if (readyProductsToUpdateToPending.length > 0) {
          console.log(`Updating ${readyProductsToUpdateToPending.length} READY products to PENDING for initial verification`);
          const updatePromises = readyProductsToUpdateToPending.map((prodInst) => cadTool.updateProduct({
            ...prodInst,
            projectId: project.id,
            billOfMaterials: 'PENDING' as BomStatus,
          }, null, prodInst.id).toPromise());
          await Promise.all(updatePromises);
        }
      } else {
        setChecksComplete(true);
        console.log('No updates required, checks complete.');
        setCheckingProducts(false);
        // Mark initial check as complete if this was the first check
        if (needsInitialCheck) {
          setProjectIdWithCompletedInitialCheck(project.id);
        }
        return;
      }

      // If there are any instance ids not in the db (maybe in a different environment's db or removed for some reason) > save as new
      if (productInstancesToCreate.length) {
        const createPromises = [];
        for (const prodInst of productInstancesToCreate) {
          createPromises.push(async () => {
            const createdProdInstResult = await createProductInstance({
              variables: {
                data: {
                  productId: prodInst.productId,
                  projectId: project.id,
                  configuration: prodInst.configuration,
                  properties: prodInst.properties ? JSON.parse(prodInst.properties) : null,
                  shadedView: prodInst.shadedView,
                },
              },
            });
            if (createdProdInstResult.data.createProductInstance) {
              const createdInstance = createdProdInstResult.data.createProductInstance;
              await cadTool.updateProduct({
                ...createdInstance, billOfMaterials: 'PENDING' as BomStatus,
              }, null, prodInst.id).toPromise();
              console.log('INSTANCE CREATED CORRECTLY', {
                oldId: prodInst.id, newId: createdInstance.id, productName: createdInstance.productName, result: createdProdInstResult.data,
              });
            } else {
              console.log('INSTANCE DID NOT CREATE CORRECTLY', { oldId: prodInst.id, productName: prodInst.productName, result: createdProdInstResult.data });
            }
          });
        }

        console.log(`CREATING ${productInstancesToCreate.length} PRODUCTS`);
        await Promise.all(createPromises.map((p) => p()));
      }

      // If there are any makeUnique instances or instances that have a different project id to skp, duplicate them
      if (productInstancesToDuplicate.length) {
        const duplicatePromises = [];
        for (const prodInst of productInstancesToDuplicate) {
          duplicatePromises.push(async () => {
            // if the instance has come from make unique, then parse the id for the original to duplicate
            const idNoHash = prodInst.id.includes('#') ? prodInst.id.slice(0, prodInst.id.indexOf('#')) : prodInst.id;
            const duplicatedProdInstResult = await duplicateProductInstanceAsNew({
              variables: {
                data: {
                  productInstanceId: idNoHash,
                  projectId: project.id,
                  // if the instance in sketchup has an incorrect or false flag, then reset the metadata of the duplicate in the database
                  resetMetadata: prodInst.billOfMaterials !== 'READY',
                },
              },
            });
            if (duplicatedProdInstResult.data.duplicateProductInstanceAsNew) {
              const duplicateInstanceId = duplicatedProdInstResult.data.duplicateProductInstanceAsNew.newProductInstance.id;
              await cadTool.updateProduct({
                ...prodInst, id: duplicateInstanceId, projectId: project.id, billOfMaterials: isBomReady(prodInst.billOfMaterials) ? 'READY' as BomStatus : 'PENDING' as BomStatus,
              }, null, prodInst.id).toPromise();
              console.log('INSTANCE DUPLICATED CORRECTLY', {
                oldId: prodInst.id, newId: duplicateInstanceId, productName: prodInst.productName, result: duplicatedProdInstResult.data,
              });
            } else {
              console.log('INSTANCE DID NOT DUPLICATE CORRECTLY', { oldId: prodInst.id, productName: prodInst.productName, result: duplicatedProdInstResult.data });
            }
          });
        }

        console.log(`DUPLICATING ${productInstancesToDuplicate.length} PRODUCTS`);
        await Promise.all(duplicatePromises.map((p) => p()));
      }

      // If there are any out of date instances to resave, save the configs and props from sketchup
      if (productInstancesToSave.length) {
        const savePromises = [];
        for (const prodInst of productInstancesToSave) {
          savePromises.push(async () => {
            const savedProdInstResult = await saveProductInstanceConfiguration({
              variables: {
                data: {
                  productInstanceId: prodInst.id,
                  productId: prodInst.productId,
                  productName: prodInst.productName,
                  projectId: project.id,
                  configuration: prodInst.configuration,
                  properties: prodInst.properties ? JSON.parse(prodInst.properties) : null,
                  shadedView: prodInst.shadedView,
                  resetMetadata: true,
                },
              },
            });
            if (savedProdInstResult.data.saveProductInstanceConfiguration) {
              await cadTool.updateProduct({
                ...prodInst, billOfMaterials: 'PENDING' as BomStatus,
              }, null, prodInst.id).toPromise();
              console.log('INSTANCE SAVED CORRECTLY', { id: prodInst.id, productName: prodInst.productName, result: savedProdInstResult.data });
            } else {
              const info = { id: prodInst.id, productName: prodInst.productName, result: savedProdInstResult.data };
              console.log('INSTANCE DID NOT SAVE CORRECTLY', info);
              Sentry.captureMessage(`Product instance did not save correctly: ${JSON.stringify(info)}`);
            }
          });
        }
        console.log(`SAVING ${productInstancesToSave.length} PRODUCTS`);
        await Promise.all(savePromises.map((p) => p()));
      }

      // If there are any products to retrieve metadata for, retrieve it
      if (productInstancesToRetrieveMetadata.length) {
        await batchVerifyBomAndUpdateSkp([], productInstancesToRetrieveMetadata, cadTool);
      }

      // Mark initial check as complete after verification to prevent infinite loops
      if (needsInitialCheck) {
        setProjectIdWithCompletedInitialCheck(project.id);
        console.log(`Initial check complete for project ${project.id}`);
      }

      // After any operations (create, duplicate, save, retrieve metadata), check if we need to run another round
      // If we just created/duplicated/saved products, they're now PENDING and need BOM retrieval
      // Always trigger another check round in this case - don't check final state yet
      const justCreatedOrSaved = productInstancesToCreate.length > 0
        || productInstancesToDuplicate.length > 0
        || productInstancesToSave.length > 0;

      if (justCreatedOrSaved) {
        // Products were just created/saved - they're now PENDING and need BOM retrieval
        // Trigger another check round to pick them up for BOM retrieval
        console.log('Products were just created/saved/duplicated, triggering another check round for BOM retrieval');
        setCheckingProducts(false);
        // Small delay to ensure SketchUp updates are processed before triggering next check
        await new Promise<void>((resolve) => {
          setTimeout(() => resolve(), BATCH_DELAY_MS);
        });
        setChecksComplete(false);
        return;
      }

      // Only check final state if we were retrieving metadata (not creating/saving)
      // Re-fetch products from SketchUp to get updated status
      const updatedProducts = await cadTool.findProducts().toPromise();
      // Filter out hash IDs (makeUnique instances), but don't filter by documentId
      // because newly created products might not have documentId set yet
      const validUpdatedProducts = updatedProducts.filter((p) => !p.id.includes('#'));

      // Debug logging to see what billOfMaterials values we're getting
      const bomStatuses = validUpdatedProducts.map((p) => ({
        id: p.id,
        name: p.productName,
        bomStatus: p.billOfMaterials,
        hasDocumentId: !!p.documentId,
      }));
      console.log('Final state check - BOM statuses:', bomStatuses);

      // Check if all products are in a final state
      // A product is in final state if:
      // - READY
      // - FAILED and retries exhausted (or non-retryable)
      // A product is NOT in final state if:
      // - PENDING
      // - FAILED but retries still ongoing (should show as PENDING in UI)
      const allInFinalState = validUpdatedProducts.length === 0 || validUpdatedProducts.every(
        (p) => {
          if (isBomReady(p.billOfMaterials)) {
            return true;
          }
          if (isBomFailed(p.billOfMaterials)) {
            const retryState = bomRetryState?.[p.instanceId];
            // If no retry state, it's a new failure (will be checked and retried if retryable)
            // If retryable and not exhausted, still retrying
            // Otherwise, retries exhausted or non-retryable - final state
            return retryState ? (retryState.retriesExhausted || !retryState.isRetryable) : false;
          }
          // PENDING or undefined - not in final state
          return false;
        },
      );

      if (allInFinalState) {
        // All products are in final state - checks are complete
        console.log('All products are in final state (READY or FAILED), setting checksComplete to true');
        setCheckingProducts(false);
        setChecksComplete(true);
        // Note: prevProductsInSkp will be updated in the useEffect when it detects the change
        // This prevents redundant checks when productsInSketchup observable emits again
      } else {
        // Some products are still PENDING or undefined - trigger another check round
        const pendingCount = validUpdatedProducts.filter(
          (p) => isBomPending(p.billOfMaterials),
        ).length;
        console.log(`${pendingCount} products still PENDING, triggering another check round`);
        // Set checkingProducts to false first, then checksComplete to false
        // This ensures the useEffect can trigger the next check round
        setCheckingProducts(false);
        // Small delay to ensure state updates are processed before triggering next check
        await new Promise<void>((resolve) => {
          setTimeout(() => resolve(), STATE_UPDATE_DELAY_MS);
        });
        // Trigger another check by setting checksComplete to false
        // The useEffect will pick this up and run checkAndUpdateProducts again
        setChecksComplete(false);
      }
    } catch (error) {
      console.error('Error when executing Check and Update Products', error);
      Sentry.captureException(error);
    }
  };

  useEffect(() => {
    (async () => {
      console.log('UE: productsInSketchup, project, appStatus, checkingProducts', {
        productsInSketchup,
        project,
        appStatus,
        checkingProducts,
      });
      if (!project?.id || (appStatus && appStatus.current_path !== appStatus.project_path)) {
        console.log('Not ready to conduct checks - project not yet established.');
        return;
      }

      // TODO: Does appStatus need to be a dependancy here?
      const appStatusHasChanged = !isEqual(appStatus, prevAppStatus);
      if (appStatusHasChanged) {
        console.log('UE: appStatus has changed', { appStatus, prevAppStatus });
        setPrevAppStatus(appStatus);
      }

      const projectHasChanged = !isEqual(project, prevProject);
      if (projectHasChanged) {
        console.log('UE: project has changed', { project, prevProject });
        setPrevProject(project);
      }

      // Check if products have changed (using isEqual to catch configuration changes, not just ID changes)
      const productsInSketchupHaveChanged = !isEqual(productsInSketchup, prevProductsInSkp);

      // Check if we just transitioned from checking to not checking
      // Only skip if we just finished AND nothing else has changed (prevents redundant run after completion)
      // But always check if products changed, even if checks were just complete
      const justFinishedChecking = prevCheckingProductsRef.current && !checkingProducts && checksComplete
        && !appStatusHasChanged && !projectHasChanged && !productsInSketchupHaveChanged;

      // Only trigger checks if:
      // 1. Not already checking
      // 2. Not saving
      // 3. Not just finished checking with no changes (prevents redundant run after completion)
      // 4. AND one of: appStatus changed, project changed, products changed, or checks not complete
      const shouldCheck = !checkingProducts && !isSaving && !justFinishedChecking && (
        appStatusHasChanged
        || projectHasChanged
        || productsInSketchupHaveChanged
        || !checksComplete
      );

      // Update ref to track checkingProducts state for next render
      prevCheckingProductsRef.current = checkingProducts;

      if (shouldCheck) {
        if (productsInSketchupHaveChanged) {
          console.log('UE: productsInSketchup have changed', { productsInSketchup, prevProductsInSkp });
        }
        console.log('UE: checks not complete, checking products...');
        // Don't set checksComplete(false) here - checkAndUpdateProducts will set it if needed
        // Setting it here causes an extra useEffect run because checksComplete is in the dependency array
        setPrevProductsInSkp(productsInSketchup);
        await checkAndUpdateProducts();
      } else if (productsInSketchupHaveChanged) {
        // Products changed but checks are complete - update prevProductsInSkp silently
        // This handles the case where observable emits again after we've completed checks
        setPrevProductsInSkp(productsInSketchup);
      }
    })();
  }, [productsInSketchup, project, appStatus, checkingProducts, checksComplete, isSaving]);

  // When finished saving, set checksComplete to false
  useEffect(() => {
    console.log('UE: isSaving', isSaving);
    if (!isSaving) {
      setChecksComplete(false);
    }
  }, [isSaving]);

  useEffect(() => {
    console.log('UE: selectedProducts', selectedProducts);
    if (selectedProducts && selectedProducts.length === 1 && showDuplicateModal) {
      setShowDuplicateModal(false);
    }
  }, [selectedProducts]);

  return (
    <ToastProvider>
      <div>
        <Head>
          <title>FJS Configurator</title>
          <link rel="preconnect" href="https://fonts.googleapis.com" />
          <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="true" />
          <link
            href="https://fonts.googleapis.com/css2?family=Arvo:wght@400;600&display=swap"
            rel="stylesheet"
          />
          <link
            href="https://fonts.googleapis.com/css2?family=Montserrat:wght@400;600;700&display=swap"
            rel="stylesheet"
          />
        </Head>
        { user && <Header homeIcon size="Small" bgColour="LightGrey" pages={userContext.userLoggedIn?.isLoggedIn ? pagesLoggedIn : pagesLoggedOut} />}
        <PriceDataContext.Provider value={{ globalPriceData, setGlobalPriceData }}>
          <ProjectContext.Provider value={project}>
            <main className="flex flex-col min-h-screen">
              <div className="flex-grow bg-fjsLightGrey">
                {children}
              </div>
            </main>
          </ProjectContext.Provider>
        </PriceDataContext.Provider>
      </div>
      <footer className="fixed bottom-0 w-full bg-fjsBlue h-9 text-fjsLightGrey text-xs flex justify-center items-center z-20 px-4">
        {process.env.NEXT_PUBLIC_APP_URL.includes('localhost') && (
          <span className="bg-fjsGreen rounded-md py-0.5 px-2 text-[12px] text-fjsWhite absolute left-2 bottom-2">Local</span>
        )}
        {process.env.NEXT_PUBLIC_APP_URL.includes('fjs-api.herokuapp.com') && (
          <span className="bg-fjsOrange rounded-md py-0.5 px-2 text-[12px] text-fjsWhite absolute left-2 bottom-2">Staging</span>
        )}
        <Link target="_blank" href="https://www.joinerysystems.com/">www.joinerysystems.com</Link>
        <div className="absolute right-4 tracking-widest">{versionName}</div>
      </footer>
    </ToastProvider>
  );
}

export default Layout;

Layout.propTypes = {
  children: PropTypes.node,
};
